/** The four difficulty levels.
 *
 * They differ in exactly two things: how deep the search goes, and how hard the
 * AI works to exploit the information it was legitimately handed. They do
 * **not** differ in resources, growth, or sight. An AI that reads fog or is
 * handed gold is not a harder opponent, it is a different game, and it makes
 * every balance measurement taken against it worthless (I9).
 *
 * The other half of a classic difficulty level - the part this file is forbidden
 * to express - is the HUMAN's handicap: the player starts with a smaller purse
 * the higher he sets the level, down to nothing at all on Impossible, while
 * every computer opponent is handed the Normal purse at every level. That table
 * is `STARTING_RESOURCES` in the kernel (`core/adventure/create.ts`), it is
 * applied once when the game is built, and it is why "Impossible" can be harder
 * than "hard" without a single number here becoming a handout.
 *
 * Every field below is a budget or a weight the planner reads. Nothing here
 * reaches the kernel, so raising a number can make the AI slow but can never
 * make it illegal.
 */

export type DifficultyId = "easy" | "normal" | "hard" | "impossible";

export interface DifficultyProfile {
  readonly id: DifficultyId;
  /** Destinations scored per hero per turn - the width of the strategic search. */
  readonly candidateLimit: number;
  /** Real-kernel battle rollouts allowed per turn. See `simulate.ts`; there is
   * no closed-form estimator, so a probe is a real battle and costs real time. */
  readonly battleProbes: number;
  /** Rounds one probe runs before it is scored as an unresolved slugging match. */
  readonly probeRounds: number;
  /** Commands one probe may spend, so a pathological battle cannot hang a turn. */
  readonly probeCommands: number;
  /** Tactical lookahead: 0 counts the blow, 1 the retaliation, 2 the next round. */
  readonly battleLookahead: 0 | 1 | 2;
  /**
   * Plans routes that take more than one day.
   *
   * Not a difficulty knob any more, and the reason is that it was never one.
   * With this off, `nearest` refuses any cell the hero cannot stand on *today*,
   * so a capital sixteen tiles away is not a target - it is not even a
   * candidate - and the level it was off for could not finish a game against a
   * player already reduced to one town and no heroes. Measured by
   * `qa/play-conquest.mjs` on Small / one opponent / Easy: the computer killed
   * the human's heroes twenty-three times over, took one of his two towns, and
   * then walked in circles until the forty-day cap. An opponent that cannot
   * conclude is not an easy opponent, it is a broken one.
   *
   * What makes a level easy stays where a designer can read it: a narrower
   * search (`candidateLimit`), no real battle rollouts (`battleProbes`), no
   * memory of what the fog has hidden (`useMemory`), a bigger margin demanded
   * before it will fight (`attackRatioFp`), less appetite for a town
   * (`aggressionBp`) and no patience for the march (`conquestPatience`, which
   * at 1 prices a capital exactly like a chest and is why an easy computer
   * still takes the near thing over the right one).
   */
  readonly multiDay: boolean;
  /** Reads the stale last-seen snapshots in the observation. Never live truth. */
  readonly useMemory: boolean;
  /**
   * Own-strength over enemy-strength, fixed point at 1000, required before the
   * AI walks into a fight it cannot probe. 2000 means "twice their army".
   */
  readonly attackRatioFp: number;
  /** Gold held back from recruiting for the next building, in basis points. */
  readonly buildReserveBp: number;
  /** Candidate evaluations per `chooseCommand` before it plays its best so far. */
  readonly evalBudget: number;
  /** Commands the AI will spend in one turn before it ends the turn regardless. */
  readonly commandsPerTurn: number;

  // ---- the war. Everything below decides whether the AI is an opponent or an
  // obstacle, and it is the half difficulty was missing: an easy computer that
  // marched on a capital exactly as hard as an impossible one is not an easy
  // computer, it is the same computer with a smaller search.

  /**
   * How far ahead a march is planned, as a divisor on the movement cost of a
   * conquest target.
   *
   * Everything else on the map is scored as value over distance, which is right
   * for a chest and wrong for a capital: a town twenty tiles away is worth the
   * same as a town two tiles away, it just takes longer, and dividing its value
   * by the walk is what made every AI ignore every town it could not already
   * touch. Patience is lookahead measured in days rather than in plies, so it is
   * the same axis difficulty is allowed to move (it never changes what the AI
   * may see). 1 means "score a town like a chest"; 6 means "a capital six days
   * away is still the best thing on the board".
   */
  readonly conquestPatience: number;
  /** Weight on an enemy town or hero, in basis points of its base value. */
  readonly aggressionBp: number;
  /**
   * Force concentration: the share of the *best army the kingdom has* that a
   * hero must carry before he is allowed to march on a town. Ten thousand means
   * "only the hero holding the main army"; a low number sends scouts at forts
   * one at a time and loses them, which is exactly what an easy opponent should
   * do.
   *
   * Measured against the strongest hero rather than against the kingdom's whole
   * field, and the difference is the difference between a rule and a deadlock:
   * with four heroes and a garrison, no hero ever holds sixty per cent of
   * everything, so a threshold read that way declines every march forever. On
   * seed 16838 it declined 3282 of 3593, while the probe it was standing in
   * front of would have said "win" three hundred times.
   */
  readonly massBp: number;
  /** Tiles from one of our towns at which an enemy hero counts as a threat. */
  readonly defendRadius: number;
  /** Heroes this AI will pay for. More heroes is more map, and more upkeep. */
  readonly maxHeroes: number;
}

const EASY: DifficultyProfile = Object.freeze({
  id: "easy",
  candidateLimit: 24,
  // Few and shallow rather than none. With no probe at all, `assessGuard` falls
  // back to a strength ratio, and `attackRatioFp` 2200 against a presumed town
  // defence that is already near parity with the whole kingdom is a threshold
  // no hero can ever clear - so this level could not take a town under any
  // circumstances, which is not an easy opponent but an absent one. Four
  // rollouts of eight rounds with no lookahead is still an easy opponent: it
  // sees the fight, and it sees it badly.
  battleProbes: 4,
  probeRounds: 8,
  probeCommands: 200,
  battleLookahead: 0,
  multiDay: true,
  useMemory: false,
  attackRatioFp: 2200,
  buildReserveBp: 0,
  evalBudget: 600,
  commandsPerTurn: 60,
  // One day of lookahead on a march, which is the smallest number that is still
  // a march. At 1 a capital is priced exactly like a chest the same distance
  // away, so an easy computer never walks past the second-nearest mine and the
  // game does not end: `qa/play-conquest.mjs`, whose whole objective is a
  // conclusion inside forty days at this level, hit its cap with the human down
  // to one town. Two still reads as impatient beside Normal's four.
  conquestPatience: 2,
  aggressionBp: 6000,
  massBp: 2500,
  defendRadius: 4,
  maxHeroes: 2,
});

const NORMAL: DifficultyProfile = Object.freeze({
  id: "normal",
  candidateLimit: 96,
  battleProbes: 6,
  probeRounds: 20,
  probeCommands: 400,
  battleLookahead: 1,
  multiDay: true,
  useMemory: true,
  attackRatioFp: 1400,
  buildReserveBp: 2500,
  evalBudget: 3000,
  commandsPerTurn: 120,
  conquestPatience: 4,
  aggressionBp: 11000,
  massBp: 6000,
  defendRadius: 7,
  maxHeroes: 3,
});

const HARD: DifficultyProfile = Object.freeze({
  id: "hard",
  candidateLimit: 256,
  battleProbes: 24,
  probeRounds: 40,
  probeCommands: 800,
  battleLookahead: 2,
  multiDay: true,
  useMemory: true,
  attackRatioFp: 1150,
  buildReserveBp: 4000,
  evalBudget: 12000,
  commandsPerTurn: 200,
  conquestPatience: 7,
  aggressionBp: 15000,
  massBp: 8000,
  defendRadius: 10,
  maxHeroes: 4,
});

/**
 * Impossible is Hard with the brakes off, in the two directions difficulty is
 * allowed to move.
 *
 * Deeper: every search budget roughly doubles, and the fight it is about to
 * pick is probed forty times over sixty rounds rather than twenty-four over
 * forty, so it commits on a measured answer where Hard commits on a guess.
 * That is what buys the thinner `attackRatioFp` - a lower margin is only
 * "better play" for a planner that can actually see the fight through; give it
 * to Easy, which probes nothing, and it walks into losing battles.
 *
 * Further: it will chase a capital nine days away, wants an enemy town nearly
 * twice as much as its cash value, and holds a fifth of its purse back for the
 * next building instead of spending everything the week it arrives. It keeps a
 * fifth hero, and it watches twelve tiles of ground around its towns.
 *
 * `battleLookahead` does not move, because 2 is the deepest the tactical scorer
 * knows how to look; a fifth level would need a deeper scorer, not a bigger
 * number here.
 */
const IMPOSSIBLE: DifficultyProfile = Object.freeze({
  id: "impossible",
  candidateLimit: 512,
  battleProbes: 40,
  probeRounds: 60,
  probeCommands: 1200,
  battleLookahead: 2,
  multiDay: true,
  useMemory: true,
  attackRatioFp: 1050,
  buildReserveBp: 5000,
  evalBudget: 24000,
  commandsPerTurn: 260,
  conquestPatience: 9,
  aggressionBp: 18000,
  massBp: 8500,
  defendRadius: 12,
  maxHeroes: 5,
});

export const DIFFICULTIES: Readonly<Record<DifficultyId, DifficultyProfile>> =
  Object.freeze({ easy: EASY, normal: NORMAL, hard: HARD, impossible: IMPOSSIBLE });

export const DIFFICULTY_IDS: readonly DifficultyId[] =
  Object.freeze(["easy", "normal", "hard", "impossible"] as const);

export function resolveDifficulty(
  d: DifficultyId | DifficultyProfile | undefined,
): DifficultyProfile {
  if (d === undefined) return NORMAL;
  if (typeof d === "string") return DIFFICULTIES[d];
  return d;
}
